import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import type { ReactElement } from "react";
import { forwardRef, useMemo, useState } from "react";
import { Thumbnail, type AvatarTone } from "../Thumbnail";
import Icon from "../../../base/components/Icon";
import {
    avatarToneFromSeed,
    profileInitialsFromName,
} from "../../utils/profileAvatar";
import { cx } from "../../utils/cx";
import styles from "./ProfileMenu.module.scss";

/** One row between the greeting and sign out (e.g. Accounts, Apps from `userMenu`). */
export type ProfileMenuItem = {
    /** Stable key for React lists (e.g. route path). */
    id: string;
    label: string;
    /** Key for {@link Icon} in `src/base/components/Icon.jsx` (e.g. `accounts`, `apps-icon`). */
    icon: string;
    onSelect: () => void;
};

export type ProfileMenuProps = {
    /** First name or display handle shown after the greeting prefix. */
    userName: string;
    /** Shown in bold before `userName` (default `Hi,`). */
    greetingPrefix?: string;
    /**
     * Two-letter (or shorter) initials on the default trigger. If omitted,
     * derived from `userName` via {@link profileInitialsFromName}.
     */
    initials?: string;
    /**
     * Pastel fill for `Thumbnail` avatar-initials. If omitted, computed from
     * `avatarColorSeed` (or `userName`) with {@link avatarToneFromSeed} so each
     * user keeps a stable color without backend support.
     */
    avatarTone?: AvatarTone;
    /**
     * Stable string for tone hashing (e.g. `user.id`, email). Defaults to
     * `userName` when `avatarTone` is not set.
     */
    avatarColorSeed?: string;
    /**
     * Navigation rows between the greeting and sign out, usually from
     * `userMenu.map` + permissions + `history.push(route)`.
     */
    items?: ProfileMenuItem[];
    /** Sign-out row label. Default `Sign out`. */
    signOutLabel?: string;
    onSignOut?: () => void;
    /** Disables the trigger and menu. */
    disabled?: boolean;
    /** `aria-label` on the default trigger (avatar-only button). */
    "aria-label"?: string;
    align?: "start" | "center" | "end";
    side?: "top" | "right" | "bottom" | "left";
    sideOffset?: number;
    className?: string;
    /**
     * Custom trigger (must be a single element that accepts a ref — e.g.
     * `forwardRef` button or `AppHeader.MenuTrigger` with `asChild`).
     * Responsive trigger height (40px on mobile) applies only to the default trigger.
     */
    trigger?: ReactElement;
};

type DefaultTriggerProps = {
    className?: string;
    disabled?: boolean;
    "aria-label": string;
    initials: string;
    avatarTone: AvatarTone;
};

const DefaultTrigger = forwardRef<HTMLButtonElement, DefaultTriggerProps>(
    function DefaultTrigger(
        {
            className,
            disabled,
            "aria-label": ariaLabel,
            initials,
            avatarTone,
            ...rest
        },
        ref,
    ) {
        return (
            <button
                aria-label={ariaLabel}
                className={cx(styles.triggerAvatarOnly, className)}
                disabled={disabled}
                ref={ref}
                type="button"
                {...rest}
            >
                <Thumbnail
                    alt=""
                    avatarTone={avatarTone}
                    initials={initials}
                    size="xs"
                    variant="avatar-initials"
                />
            </button>
        );
    },
);

DefaultTrigger.displayName = "ProfileMenuTrigger";

/**
 * Profile dropdown aligned with Figma **SecondaryNav-Profile** (surface, shadow,
 * typography, row padding).
 *
 * Default trigger: **only** the circular `Thumbnail` (avatar-initials); it opens
 * and closes the menu. Avatar uses **`sm` (48px)** on desktop; **40×40px** below 768px.
 *
 * Middle rows come from **`items`** (labels + {@link Icon} keys + `onSelect`).
 * Last row is always **Sign out** (`signOutLabel` + `onSignOut`).
 */
export function ProfileMenu({
    userName,
    greetingPrefix = "Hi,",
    initials: initialsProp,
    avatarTone: avatarToneProp,
    avatarColorSeed,
    items = [],
    signOutLabel = "Sign out",
    onSignOut,
    disabled,
    "aria-label": ariaLabel,
    align = "end",
    side = "bottom",
    sideOffset = 6,
    className,
    trigger,
}: ProfileMenuProps) {
    const [open, setOpen] = useState(false);
    const resolvedInitials = useMemo(
        () => initialsProp ?? profileInitialsFromName(userName),
        [initialsProp, userName],
    );
    const resolvedAvatarTone = useMemo(() => {
        if (avatarToneProp) {
            return avatarToneProp;
        }
        return avatarToneFromSeed(avatarColorSeed ?? userName);
    }, [avatarToneProp, avatarColorSeed, userName]);

    const defaultTriggerLabel = (
        ariaLabel ?? `Account menu, ${userName}`
    ).trim();

    return (
        <DropdownMenu.Root onOpenChange={setOpen} open={open}>
            <DropdownMenu.Trigger asChild>
                {trigger ?? (
                    <DefaultTrigger
                        aria-label={defaultTriggerLabel}
                        avatarTone={resolvedAvatarTone}
                        className={className}
                        disabled={disabled}
                        initials={resolvedInitials}
                    />
                )}
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
                <DropdownMenu.Content
                    align={align}
                    className={styles.content}
                    loop
                    side={side}
                    sideOffset={sideOffset}
                >
                    <DropdownMenu.Label className={styles.greeting}>
                        <span className={styles.greetingStrong}>
                            {greetingPrefix}
                        </span>{" "}
                        {userName}
                    </DropdownMenu.Label>
                    <div className={styles.headerRule} role="presentation" />

                    {items.map(item => (
                        <DropdownMenu.Item
                            className={styles.item}
                            disabled={disabled}
                            key={item.id}
                            onSelect={() => item.onSelect()}
                        >
                            <span className={styles.itemIcon}>
                                <Icon icon={item.icon} />
                            </span>
                            {item.label}
                        </DropdownMenu.Item>
                    ))}

                    <DropdownMenu.Item
                        className={styles.item}
                        disabled={disabled}
                        onSelect={() => onSignOut?.()}
                    >
                        <span className={styles.itemIcon}>
                            <Icon icon="sign-out" />
                        </span>
                        {signOutLabel}
                    </DropdownMenu.Item>
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
}

ProfileMenu.displayName = "ProfileMenu";
